import { useRef, useCallback } from 'react';

/**
 * Hook pour créer une fonction avec debounce qui empêche les appels multiples rapides
 * @param callback - La fonction à exécuter
 * @param delay - Le délai en millisecondes (par défaut 500ms)
 * @returns Un tuple [fonction debounced, état isProcessing]
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 500
): [T, boolean] {
  const isProcessing = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      // Si déjà en train de traiter, ignorer
      if (isProcessing.current) {
        return;
      }

      // Marquer comme en traitement
      isProcessing.current = true;

      // Exécuter le callback
      callback(...args);

      // Clear timeout existant
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Réinitialiser après le délai
      timeoutRef.current = setTimeout(() => {
        isProcessing.current = false;
      }, delay);
    }) as T,
    [callback, delay]
  );

  return [debouncedCallback, isProcessing.current];
}
